import { supabase } from '@/integrations/supabase/client';
import type { CreateOrgDto } from '../types/organization.types';

export interface PublicInvitationDetails {
  id: string;
  email: string;
  organization_name: string;
  role: string;
  expires_at: string;
  invited_by_name: string;
}

export interface AcceptInvitationResult {
  success: boolean;
  error?: string;
  organization_id?: string;
  role?: string;
}

export const publicFlowsService = {
  /**
   * Creates an organization with the current authenticated user as owner.
   * Runs context-free (used during onboarding).
   */
  async createOrganizationWithOwner(dto: CreateOrgDto): Promise<string> {
    const { data, error } = await supabase.rpc('create_organization_with_owner', {
      p_name: dto.name,
      p_organization_type: dto.organizationType,
    });

    if (error) {
      throw error;
    }

    return data as string;
  },

  /**
   * Fetches public invitation details by token.
   * Validates server-side via RPC.
   */
  async getInvitationDetails(token: string): Promise<PublicInvitationDetails | null> {
    const { data, error } = await supabase.rpc('get_invitation_details', { token });
    if (error) throw error;
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    return (Array.isArray(data) ? data[0] : data) as PublicInvitationDetails;
  },

  /**
   * Accepts an invitation by token for the specified user ID.
   * Runs context-free via RPC.
   */
  async acceptInvitation(token: string, userId: string): Promise<AcceptInvitationResult> {
    const { data, error } = await supabase.rpc('accept_invitation', {
      invitation_token: token,
      user_id: userId,
    });

    if (error) throw error;
    return (data as unknown) as AcceptInvitationResult;
  },
};
